import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { ExecuteWorkflowNodeRequest } from '@unifyapps/network/generated/models/executeWorkflowNodeRequest';
import useSSEQuerySubscription, {
  SSE_QUERY_OPTIONS,
} from '@unifyapps/network/hooks/useSSEQuerySubscription';

import type { JSONSchema7 } from 'json-schema';
import { useMemo } from 'react';
import type { QueryRequestResult } from '../../../../../stores/GlobalStateStore';
import type { DataSourceQueryOptions } from '../../../types';
import { type DebugParams, useExecuteWorkflowNodeQueryKeyMemo } from '../api';
import { useGetSSEMergeFunction } from './useGetSSEMergeFunction';

type UseStreamDataSourceWorkflowNodeParams = {
  payload: ExecuteWorkflowNodeRequest;
  debugParams: DebugParams;
  queryOptions?: Partial<DataSourceQueryOptions>;
  enabled?: boolean;
  interfaceRecord?: InterfaceEntity;
};

export type StreamDataSourceWorkflowNodeQueryRequestResult = QueryRequestResult & {
  outputSchema?: JSONSchema7;
  streamingText?: string;
};

function useStreamDataSourceWorkflowNode({
  payload,
  debugParams,
  queryOptions,
  enabled,
}: UseStreamDataSourceWorkflowNodeParams): StreamDataSourceWorkflowNodeQueryRequestResult {
  const queryKeyType = 'sse';
  const queryKey = useExecuteWorkflowNodeQueryKeyMemo(payload, debugParams, queryKeyType);

  // SSE streaming query for text concatenation
  // todo: this will be updated when the backend returns a proper open api like streaming response which we can merge onto data directly
  const updateSSEPath = 'response.result.data';
  const { sseMergeFunction } = useGetSSEMergeFunction({ updateSSEPath });

  const {
    isLoading,
    isFetching,
    error: fetchError,
    data,
    refetch,
  } = useSSEQuerySubscription<unknown, unknown>(
    {
      queryKey,
      enabled,
      ...queryOptions,
      ...SSE_QUERY_OPTIONS,
    },
    sseMergeFunction,
  );

  const adaptedData = useMemo(() => {
    // response.result is moved to response level for now
    const result = (data as { response: { result: unknown } } | undefined)?.response?.result;
    if (result !== undefined) {
      // Validate that result is an object before spreading
      if (typeof result === 'object' && result !== null) {
        return {
          ...result,
        };
      }
      // Return result as-is if it's not an object
      return result;
    }
    return data;
  }, [data]);

  const processedError = useMemo(() => {
    if (!fetchError) return null;

    // Check if error is an object and has payload property
    if (typeof fetchError === 'object' && fetchError !== null && 'payload' in fetchError) {
      return (fetchError as { payload: unknown }).payload;
    }

    return fetchError;
  }, [fetchError]);

  return {
    error: processedError,
    isLoading,
    isFetching,
    rawData: adaptedData,
    data: adaptedData,
    queryKey,
    refetch,
    // todo: fetch and set proper output schema when we handling merge fnc with proper standard
    outputSchema: {
      type: 'object',
      properties: {
        data: {
          type: 'string',
        },
      },
      required: ['data'],
    },
  };
}

export default useStreamDataSourceWorkflowNode;
